// Depo bazlı e-Belge Tanımları (paylaşılan komponent)
// Kullanım: <WarehouseEDocSettings warehouseId={id} canEdit={true} />
import React, { useEffect, useState, useCallback } from 'react';
import api from '../services/api';
import { Button } from './ui';

const E_DOC_LABELS = {
  eFatura:    { label: 'e-Fatura',     defaultPrefix: 'EF',  hint: 'Yurt içi GİB e-Fatura mükellefi alıcılar' },
  eArsiv:     { label: 'e-Arşiv',      defaultPrefix: 'EA',  hint: 'Yurt içi GİB mükellef olmayan alıcılar' },
  eIhracat:   { label: 'e-İhracat',    defaultPrefix: 'EH',  hint: 'Yurt dışı ihracat faturaları' },
  eIrsaliye:  { label: 'e-İrsaliye',   defaultPrefix: 'EI',  hint: 'Sevk irsaliyesi' },
  eSMM:       { label: 'e-SMM',        defaultPrefix: 'SM',  hint: 'Serbest Meslek Makbuzu' },
  eMustahsil: { label: 'e-Müstahsil',  defaultPrefix: 'MM',  hint: 'Tarım üreticileri için müstahsil makbuzu' },
};

export default function WarehouseEDocSettings({ warehouseId, canEdit = true }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = useCallback(async () => {
    if (!warehouseId) { setList([]); return; }
    setLoading(true);
    try {
      const r = await api.get(`/warehouses/${warehouseId}/edocument-settings`);
      if (r.data.success) setList(r.data.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [warehouseId]);

  useEffect(() => { load(); }, [load]);

  const update = (docType, field, value) => {
    setList(prev => prev.map(s => {
      if (s.doc_type !== docType) return s;
      const next = { ...s, [field]: value };
      // Aktif edildiğinde prefix boşsa default prefix'i otomatik doldur
      if (field === 'is_enabled' && value && !next.prefix) {
        next.prefix = E_DOC_LABELS[docType]?.defaultPrefix || '';
      }
      return next;
    }));
  };

  const save = async () => {
    setSaving(true); setMsg(null);
    try {
      // Aktif ama prefix boş kayıtlara default'u uygula (kaydetmeden önce)
      const payload = list.map(s => ({
        ...s,
        prefix: s.is_enabled && !s.prefix
          ? (E_DOC_LABELS[s.doc_type]?.defaultPrefix || s.prefix)
          : s.prefix,
      }));
      await api.put(`/warehouses/${warehouseId}/edocument-settings`, { settings: payload });
      setMsg({ type: 'success', text: 'Kaydedildi' });
      setTimeout(() => setMsg(null), 2000);
      await load();
    } catch (e) { setMsg({ type: 'error', text: e.response?.data?.message || 'Hata' }); }
    finally { setSaving(false); }
  };

  if (!warehouseId) return <div style={{ padding: 16, color: 'var(--text-tertiary)' }}>Önce bir depo seçin.</div>;
  if (loading) return <div style={{ padding: 16 }}>Yükleniyor...</div>;

  return (
    <div>
      {msg && <div style={{ padding: 10, marginBottom: 12, borderRadius: 4, background: msg.type === 'error' ? 'rgba(220,38,38,0.08)' : 'rgba(16,185,129,0.08)', color: msg.type === 'error' ? '#991b1b' : 'var(--success)' }}>{msg.text}</div>}

      <table className="stocks-table" style={{ minWidth: 600 }}>
        <thead>
          <tr>
            <th style={{ width: 80, textAlign: 'center' }}>Aktif</th>
            <th>Belge Tipi</th>
            <th style={{ width: 130 }}>Ön Ek</th>
            <th style={{ width: 130 }}>Sıradaki No</th>
          </tr>
        </thead>
        <tbody>
          {list.map(s => {
            const meta = E_DOC_LABELS[s.doc_type] || { label: s.doc_type, defaultPrefix: '' };
            return (
              <tr key={s.doc_type} className="stocks-table-row">
                <td style={{ textAlign: 'center' }}>
                  <input type="checkbox" checked={!!s.is_enabled} disabled={!canEdit}
                    onChange={e => update(s.doc_type, 'is_enabled', e.target.checked)}
                    style={{ width: 18, height: 18, cursor: canEdit ? 'pointer' : 'default' }} />
                </td>
                <td>
                  <strong>{meta.label}</strong>
                  <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{meta.hint}</div>
                </td>
                <td>
                  <input type="text" value={s.prefix || ''} disabled={!canEdit || !s.is_enabled}
                    onChange={e => update(s.doc_type, 'prefix', e.target.value.toUpperCase())}
                    placeholder={meta.defaultPrefix}
                    maxLength={20}
                    style={{ width: '100%', padding: '6px 8px', border: '1px solid var(--border-color)', borderRadius: 4, fontFamily: 'monospace', textTransform: 'uppercase' }} />
                </td>
                <td>
                  <input type="number" value={s.next_number || 1} disabled={!canEdit || !s.is_enabled}
                    onChange={e => update(s.doc_type, 'next_number', parseInt(e.target.value) || 1)}
                    min={1}
                    style={{ width: '100%', padding: '6px 8px', border: '1px solid var(--border-color)', borderRadius: 4, fontFamily: 'monospace' }} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {canEdit && (
        <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end' }}>
          <Button size="sm" onClick={save} loading={saving}>Kaydet</Button>
        </div>
      )}
    </div>
  );
}
